import { BaseAPI } from '../runtime';
import { PlayerFromJSON, PlayerToJSON, type Player } from '../models/Player';
import { TeamFromJSON, type Team } from '../models/Team';
import { getApiConfigNext } from './index';

export type RosterPlayer = Player & { goalkeeper?: boolean };
export type TeamWithRoster = Omit<Team, 'players'> & { players?: RosterPlayer[] };

export function rosterPlayerFromJSON(value: unknown): RosterPlayer {
  const player = PlayerFromJSON(value);
  const raw = value as { goalkeeper?: boolean };
  return { ...player, goalkeeper: raw?.goalkeeper === true };
}

export function rosterPlayerToJSON(player: RosterPlayer): unknown {
  return { ...PlayerToJSON(player), goalkeeper: player.goalkeeper };
}

export function teamWithRosterFromJSON(value: unknown): TeamWithRoster {
  const team: TeamWithRoster = TeamFromJSON(value);
  const raw = value as { players?: unknown[]; permissions?: unknown };
  if (Array.isArray(raw?.players)) team.players = raw.players.map(rosterPlayerFromJSON);
  if (raw?.permissions != null) Object.assign(team, { permissions: raw.permissions });
  return team;
}

class TeamRosterApi extends BaseAPI {
  async get(teamId: string): Promise<TeamWithRoster> {
    const response = await this.request({ path: `/teams/${encodeURIComponent(teamId)}`, method: 'GET', headers: {} });
    return teamWithRosterFromJSON(await response.json());
  }

  async update(teamId: string, players: RosterPlayer[]): Promise<RosterPlayer[]> {
    const response = await this.request({
      path: `/teams/${encodeURIComponent(teamId)}/players`, method: 'PATCH',
      headers: { 'Content-Type': 'application/json' }, body: players.map(rosterPlayerToJSON),
    });
    const result: unknown = await response.json();
    return Array.isArray(result) ? result.map(rosterPlayerFromJSON) : [];
  }
}

export async function getTeamWithRoster(teamId: string): Promise<TeamWithRoster> {
  return new TeamRosterApi(await getApiConfigNext()).get(teamId);
}

export async function updateTeamRoster(teamId: string, players: RosterPlayer[]): Promise<RosterPlayer[]> {
  return new TeamRosterApi(await getApiConfigNext()).update(teamId, players);
}
